/**
 * PTT bubble: distance and time to the next reachable PTT along the route.
 *
 * Only ever shows a station that survived the corridor / ahead / side-of-road
 * filters in nav/fuelStops.ts, so tapping it can never send the driver across
 * oncoming traffic or into a U-turn.
 *
 * Tapping it opens a confirmation showing which station, how far, and which
 * side of the road it is on. The driver asked for the confirm step rather than
 * an immediate re-route: a stray touch on a dashboard mount should not silently
 * rewrite the route of a 400 km run.
 *
 * The confirmation offers TWO stations, not one, because of frontage roads. On
 * a divided highway the nearest PTT is often served by a parallel service road,
 * so committing to it means leaving the carriageway well before you can see the
 * forecourt. Being shown the one after it - usually a straightforward pull-in -
 * lets the driver make that call at a glance instead of discovering it at 90.
 */

import type { FuelStop } from '../nav/fuelStops';
import { formatDistance, formatDuration } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

export interface FuelChoice {
  stop: FuelStop;
  /** Metres from here to the station. */
  remaining: number;
  /** Seconds. */
  eta: number;
}

export class FuelBubble {
  readonly el: HTMLElement;
  private confirmEl: HTMLElement;
  private choices: FuelChoice[] = [];
  private nearest: FuelChoice | null = null;
  /** Every reachable station ahead, for the chooser. */
  private all: FuelChoice[] = [];
  /** What the sheet is currently offering, in display order. */
  private picks: FuelChoice[] = [];
  private lastUnits: UnitSystem = 'metric';

  constructor(
    parent: HTMLElement,
    private onDetour: (stop: FuelStop) => void,
  ) {
    this.el = document.createElement('button');
    this.el.className = 'fuel-bubble';
    this.el.hidden = true;
    parent.appendChild(this.el);
    this.el.addEventListener('click', () => this.openConfirm());

    this.confirmEl = document.createElement('div');
    this.confirmEl.className = 'fuel-confirm';
    this.confirmEl.hidden = true;
    parent.appendChild(this.confirmEl);
  }

  hide(): void {
    this.el.hidden = true;
    this.confirmEl.hidden = true;
    this.choices = [];
    this.all = [];
    this.nearest = null;
  }

  closeConfirm(): void {
    this.confirmEl.hidden = true;
  }

  private openConfirm(): void {
    /*
     * Nearest first, whatever the brand.
     *
     * An earlier version led with PTT on the grounds that it is the preferred
     * brand, but the question the driver is answering is "where can I stop",
     * and that is a question about distance. Brand is carried by the colour of
     * each card instead, which is readable without re-ordering anything.
     */
    const seen = new Set<string>();
    const list = [...this.all, ...this.choices, ...(this.nearest ? [this.nearest] : [])]
      .filter((c) => (seen.has(c.stop.id) ? false : seen.add(c.stop.id)))
      .sort((a, b) => a.remaining - b.remaining);
    if (!list.length) return;
    this.picks = list;
    const cards = list.map((c, i) => this.card(c, i)).join('');

    this.confirmEl.hidden = false;
    this.confirmEl.innerHTML = `
      <div class="fuel-confirm__card">
        <div class="fuel-confirm__q">${
          this.choices.length > 1 ? t('fuel.choose') : t('fuel.confirm')
        }</div>
        <div class="fuel-confirm__list">${cards}</div>
        <div class="fuel-confirm__actions">
          <button class="btn btn--ghost" data-act="cancel">${t('preview.cancel')}</button>
        </div>
      </div>`;

    this.confirmEl
      .querySelector('[data-act=cancel]')!
      .addEventListener('click', () => this.closeConfirm());
    this.confirmEl.querySelectorAll<HTMLElement>('[data-pick]').forEach((el) => {
      el.addEventListener('click', () => {
        const pick = this.picks[Number(el.dataset.pick)];
        if (!pick) return;
        this.closeConfirm();
        this.onDetour(pick.stop);
      });
    });
    this.confirmEl.addEventListener('click', (e) => {
      if (e.target === this.confirmEl) this.closeConfirm();
    });
  }

  private card(c: FuelChoice, i: number): string {
    const frontage = c.stop.access === 'frontage';
    const slug = brandSlug(c.stop.brand);
    // The name often just repeats the brand ("PTT"); only show it when it adds
    // something, so the card is a brand and a distance rather than a wall.
    const detail = c.stop.name.trim().toLowerCase() === c.stop.brand.trim().toLowerCase()
      ? ''
      : c.stop.name;
    return `
      <button class="fuel-opt fuel-opt--${slug} ${frontage ? 'is-frontage' : ''}"
              data-pick="${i}">
        <span class="fuel-opt__mark">${escapeHtml(initials(c.stop.brand))}</span>
        <span class="fuel-opt__body">
          <span class="fuel-opt__brand">${escapeHtml(c.stop.brand)}</span>
          ${detail ? `<span class="fuel-opt__name">${escapeHtml(detail)}</span>` : ''}
          <span class="fuel-opt__meta">
            <span class="fuel-opt__dist">${formatDistance(c.remaining, this.lastUnits)}</span>
            <span>${formatDuration(c.eta)}</span>
            <span>${t(c.stop.side === 'left' ? 'fuel.left' : 'fuel.right')}</span>
          </span>
          ${
            frontage
              ? `<span class="fuel-opt__flag">${t('fuel.frontage')} - ${t(
                  'fuel.frontageNote',
                )}</span>`
              : ''
          }
        </span>
      </button>`;
  }

  /** Nearest first; the bubble shows the first, the sheet offers the first two. */
  update(
    choices: FuelChoice[],
    nearest: FuelChoice | null,
    all: FuelChoice[],
    units: UnitSystem,
  ): void {
    const head = choices[0] ?? nearest;
    if (!head) {
      this.hide();
      return;
    }
    this.choices = choices;
    this.all = all;
    this.nearest = nearest && nearest.stop.id !== head.stop.id ? nearest : null;
    this.lastUnits = units;
    this.el.hidden = false;
    const frontage = head.stop.access === 'frontage';
    this.el.innerHTML = `
      <span class="fuel-bubble__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M4 21 V5 a2 2 0 0 1 2-2 h6 a2 2 0 0 1 2 2 v16 M3 21 h12
          M6 8 h6 M16 9 l3 3 v6 a1.6 1.6 0 0 0 3 0 V10 l-2.4-2.6" fill="none" stroke="currentColor"
          stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </span>
      <span class="fuel-bubble__text">
        <strong>${formatDistance(head.remaining, units)}${
          this.nearest
            ? `<small>(${formatDistance(this.nearest.remaining, units)} ${escapeHtml(
                this.nearest.stop.brand,
              )})</small>`
            : ''
        }</strong>
        <em>${formatDuration(head.eta)} · ${t(
          head.stop.side === 'left' ? 'fuel.left' : 'fuel.right',
        )}${frontage ? ` · ${t('fuel.frontage')}` : ''}</em>
      </span>`;
    this.el.setAttribute(
      'aria-label',
      `${head.stop.name}, ${formatDistance(head.remaining, units)}`,
    );
  }
}

/**
 * Brand key for colouring. The card is recognised by its colour before any of
 * the text is read - which is the point of it at 90 km/h with one hand.
 */
function brandSlug(brand: string): string {
  const b = brand.toLowerCase();
  for (const k of ['ptt', 'bangchak', 'shell', 'caltex', 'esso', 'susco']) {
    if (b.includes(k)) return k;
  }
  if (/^pt/.test(b)) return 'pt';
  return 'other';
}

/**
 * Short mark for the badge. Explicit per brand rather than "first two letters",
 * which rendered PTT as "PT" - the name of a different chain entirely.
 */
const MARKS: Record<string, string> = {
  ptt: 'PTT',
  bangchak: 'BCP',
  shell: 'SH',
  caltex: 'CX',
  esso: 'ES',
  susco: 'SUS',
  pt: 'PT',
};

function initials(brand: string): string {
  const known = MARKS[brandSlug(brand)];
  if (known) return known;
  const words = brand.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || 'F';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
